import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import { nodeApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Lock, Menu, X } from 'lucide-react';
import { FOHUserChip } from './FOHUserChip';
import { useIdleLock } from '../hooks/useIdleLock';
import { remember as rememberWhere, forget as forgetWhere } from '../lib/lockResume';
import { DrawerProvider, useDrawer } from '../contexts/DrawerContext';
import PrintingBanner from './PrintingBanner';
import { toast } from 'sonner';

// One idle countdown toast, updated in place each second.
const IDLE_TOAST = 'idle-lock';

// ================================
// FRONT-OF-HOUSE HORIZONTAL NAV (node-ui)
// ================================
//
// The terminal's whole chrome. The old single-tree `Layout.js` also carried the back-office
// sidebar and header; those live in `@verlo/cloud-ui` now (P7, plan §6). node-ui mounts no
// back-office route, so every `HAS_BACK_OFFICE` branch that drew a "Back Office" link is
// gone — there is nowhere on the Pi for it to lead. `LOGIN_PATH` is the PIN kiosk, because
// node-api mounts no `POST /auth/login`.
//
// The bar is two registers: words on the left (where am I + navigation), glyphs on the
// right (Service, lock). No vendor logo — this is an operating terminal, not a brochure.

const LOGIN_PATH = '/kiosk';

// "Where am I" — venue name over "site · till". A chain terminal shows `Pizza Express` /
// `Ealing Broadway · Till 2`; an independent just its name. Source of truth is
// `GET /node/identity` (served by node-api from its down-synced org/site rows, so it
// resolves offline); the VITE_* vars are only the first-paint / never-fetched fallback,
// and the last good answer is cached in localStorage so a reload is instant. (Auth plan §4.)
const ENV_VENUE_NAME = import.meta.env.VITE_VENUE_NAME || 'Verlo';
const ENV_TERMINAL_LABEL = import.meta.env.VITE_TERMINAL_LABEL || '';
const IDENTITY_CACHE_KEY = 'verlo_node_identity';

const readCachedIdentity = () => {
  try {
    const raw = localStorage.getItem(IDENTITY_CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* private mode / bad JSON — fall through to env */ }
  return null;
};

const fohNavItems = [
  { to: '/tables', label: 'Tables' },
  { to: '/bar', label: 'Bar' },
  { to: '/orders', label: 'Orders' },
  { to: '/kitchen', label: 'Kitchen' },
  { to: '/bookings', label: 'Bookings' },
];


const SiteTag = () => {
  const [identity, setIdentity] = useState(readCachedIdentity);

  useEffect(() => {
    let live = true;
    nodeApi.identity()
      .then((res) => {
        if (!live || !res.data) return;
        setIdentity(res.data);
        try {
          localStorage.setItem(IDENTITY_CACHE_KEY, JSON.stringify(res.data));
        } catch { /* private mode — the in-memory value still applies for this session */ }
      })
      .catch(() => { /* offline / pre-enrolment — keep the cache or env fallback */ });
    return () => { live = false; };
  }, []);

  const venueName = identity?.venue_name || ENV_VENUE_NAME;
  const terminalLabel = identity?.terminal_label ?? ENV_TERMINAL_LABEL;

  return (
    <div className="flex flex-col leading-tight shrink-0" data-testid="foh-site-tag">
      <span className="text-[13px] font-semibold text-ink">{venueName}</span>
      {terminalLabel && <span className="text-[11px] text-ink-faint">{terminalLabel}</span>}
    </div>
  );
};

// The drawer's state in the header, on a terminal that has one: "Till · £100 float" while
// a session is open, "Cashed off 23:12" after, "Till" before. A destination like Service.
const TillWord = ({ active, onClick, className }) => {
  const { hasDrawer, session, drawer, loaded } = useDrawer();
  if (loaded && !hasDrawer) return null;
  const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  // A cash-off from an earlier day says which day: "Cashed off 12:45" alone reads as today's.
  const clock = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    if (d.toDateString() !== new Date().toDateString()) return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };
  const last = drawer?.last_closed;
  const word = session ? `Till · ${gbp(session.float_opening)} float` : last ? `Cashed off ${clock(last.closed_at)}`.trim() : 'Till';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={className}
      data-testid="foh-till-btn"
      data-state={session ? 'open' : last ? 'closed' : 'none'}
    >
      {word}
    </button>
  );
};

export const FOHHeader = () => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // `service.view` is a real permission now (6b) — an owner can grant/deny it per person.
  const canSeeService = hasPermission('service.view');

  // Lock === return to the PIN screen ("done" state). Drafts are mirrored to
  // localStorage as the cart changes, so nothing in progress is lost.
  // `idle`: the till locked itself. Only then is where this person was remembered, so their
  // PIN brings them back and the pad says "Priya was on T4" (lib/lockResume.js). Someone who
  // taps Lock has finished, and the pad says nothing about them.
  const lockWith = useCallback((idle) => {
    setMobileMenuOpen(false);
    toast.dismiss(IDLE_TOAST);
    if (idle) rememberWhere(user, location.pathname + location.search);
    else forgetWhere();
    logout();
    navigate(LOGIN_PATH);
  }, [logout, navigate, user, location.pathname, location.search]);
  const handleLock = useCallback(() => lockWith(false), [lockWith]);
  const handleIdleLock = useCallback(() => lockWith(true), [lockWith]);

  // Safety net: lock after a minute idle, the last 15 seconds counted down. Not on a kitchen
  // or bar board, which sits untouched between tickets and must stay up all service; leaving
  // one after the session has run out locks then (`idleStep` has the long version).
  const onBoard = location.pathname.startsWith('/kitchen') || location.pathname.startsWith('/bar-board');
  // Whose board it is: the kitchen screen is the kitchen's (who may move its tickets on), the
  // bar board whoever's on the bar. Their touches there keep them signed in, so leaving the
  // board is not a lock; anyone else's don't (hooks/useIdleLock).
  const worksBoard = location.pathname.startsWith('/bar-board') || (location.pathname.startsWith('/kitchen') && hasPermission('kitchen.bump'));
  const { secondsLeft } = useIdleLock(handleIdleLock, { hold: onBoard, works: worksBoard });

  // The countdown is a toast: toasts sit above everything, a dialog included. It used to be a
  // bar docked under the header, which an open dialog's backdrop covered, so the till locked
  // out from under the dialog with no warning anyone could see.
  useEffect(() => {
    if (secondsLeft == null) { toast.dismiss(IDLE_TOAST); return; }
    toast.warning(`Locking in ${secondsLeft}s`, {
      id: IDLE_TOAST,
      description: 'Touch anywhere to stay signed in',
      duration: Infinity,
      className: 'idle-lock-toast',
    });
  }, [secondsLeft]);
  useEffect(() => () => toast.dismiss(IDLE_TOAST), []);

  const openService = () => navigate('/service');
  // The Service pill sits apart from the tab strip but is a destination like any tab:
  // it takes the same selected state so the header always says where you are.
  const onService = location.pathname.startsWith('/service');
  const onTill = location.pathname.startsWith('/till');
  const pill = (active) => `px-3.5 lg:px-4 py-2 rounded-lg text-[15px] font-medium transition-all whitespace-nowrap
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo ${
    active ? 'bg-verlo text-white' : 'text-ink-soft hover:text-ink hover:bg-paper'
  }`;

  return (
    <>
      {/* Desktop Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-hairline z-50 hidden md:flex items-center justify-between px-4 lg:px-6">
        {/* Left: where am I + navigation */}
        <div className="flex items-center gap-4 lg:gap-5 shrink-0">
          <SiteTag />
          <div className="w-px h-6 bg-hairline shrink-0" />
          <nav className="flex items-center gap-1" data-testid="foh-segmented-nav">
            {fohNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `inline-flex min-h-11 items-center px-3.5 lg:px-4 py-2 rounded-lg text-[15px] font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-verlo text-white'
                      : 'text-ink-soft hover:text-ink hover:bg-paper'
                  }`
                }
                data-testid={`foh-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        {/* Right: Till (drawers) + Service (managers) + who am I + lock */}
        <div className="flex items-center gap-3">
          <TillWord active={onTill} onClick={() => navigate('/till')} className={pill(onTill)} />
          {canSeeService && (
            <>
              <button
                type="button"
                onClick={openService}
                aria-current={onService ? 'page' : undefined}
                className={`px-3.5 lg:px-4 py-2 rounded-lg text-[15px] font-medium transition-all whitespace-nowrap
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo ${
                  onService
                    ? 'bg-verlo text-white'
                    : 'text-ink-soft hover:text-ink hover:bg-paper'
                }`}
                data-testid="foh-service-btn"
              >
                Service
              </button>
              <div className="w-px h-6 bg-hairline" />
            </>
          )}
          <FOHUserChip user={user} onLock={handleLock} />
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-hairline z-50 flex items-center justify-between px-4">
        <SiteTag />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="text-ink-soft"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/30 z-40" onClick={() => setMobileMenuOpen(false)} />
          <div className="md:hidden fixed top-14 left-0 right-0 bg-white border-b border-hairline z-50 p-4 space-y-1">
            {fohNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 rounded-lg font-medium ${
                    isActive ? 'bg-verlo text-white' : 'text-ink-soft'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <TillWord active={onTill} onClick={() => { setMobileMenuOpen(false); navigate('/till'); }}
              className={`flex items-center px-4 py-3 rounded-lg font-medium w-full ${onTill ? 'bg-verlo text-white' : 'text-ink-soft'}`} />
            {canSeeService && (
              <button
                onClick={() => { setMobileMenuOpen(false); openService(); }}
                aria-current={onService ? 'page' : undefined}
                className={`flex items-center px-4 py-3 rounded-lg font-medium w-full ${
                  onService ? 'bg-verlo text-white' : 'text-verlo'
                }`}
              >
                Service
              </button>
            )}
            <div className="border-t border-hairline pt-2 mt-2">
              <button
                onClick={handleLock}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-verlo font-medium w-full"
              >
                <Lock size={18} />
                Lock
              </button>
            </div>
          </div>
        </>
      )}

    </>
  );
};

export const FOHLayout = ({ children }) => {
  // Add foh-active class to body for modal styling
  React.useEffect(() => {
    document.body.classList.add('foh-active');
    return () => {
      document.body.classList.remove('foh-active');
    };
  }, []);

  return (
    <DrawerProvider>
      <div className="min-h-screen bg-surface-2 foh-layout">
        <FOHHeader />
        <main className="pt-16 md:pt-16 min-h-screen">
          <div className="p-4 md:p-6 max-w-[1600px] mx-auto">
            {/* A printer that has stopped taking tickets is said on every screen, not just
                the one someone happens to be on (#94). */}
            <PrintingBanner />
            {children}
          </div>
        </main>
      </div>
    </DrawerProvider>
  );
};

// Legacy exports
export const MainLayout = FOHLayout;
export const Sidebar = FOHHeader;

export default FOHLayout;
